import { ObjectId } from '@freightview/object-id'

import {
  vArray,
  vBoolean,
  vCurrency,
  vInt,
  vMinValue,
  vNonEmptyString,
  vNumber,
  vObject,
  vObjectId,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vRegex,
  vString,
  vStringToNumber,
  vTransform,
  vWeightUOM,
} from '../validation/index.ts'
import {
  dimensionsUOM,
  freightClasses,
  hazardClasses,
  hazardSubClasses,
  itemRefNumTypes,
} from './common.ts'
import { packagingTypes } from './packaging-type.ts'
import { packingGroups } from './packing-group.ts'

export const loadItemRefNumSchema = vObject({
  type: vPicklist(itemRefNumTypes),
  value: vString(),
  unitIndex: vOptional(vNumber()),
})

const subClass = vPipe(
  vOptional(vPicklist(hazardSubClasses)),
  vTransform(value => (!value ? undefined : value)),
)

export const baseLoadItemHazDetailSchema = vObject({
  hazmatId: vOptional(
    vPipe(
      vNonEmptyString(),
      vRegex(/^(NA|UN)\d{4}$/),
      vTransform(v => (v ? v.toString().toUpperCase() : v)),
    ),
  ),
  permitNumber: vOptional(vString()),
  packingGroup: vPipe(
    vOptional(
      vPicklist([
        ...packingGroups.map(p => p.name),
        ...packingGroups.map(p => p.key),
      ]),
    ),
    vTransform(
      value =>
        packingGroups.find(p => p.name === value || p.key === value)?.key,
    ),
  ),
  primaryClass: vOptional(vPicklist(hazardClasses.map(h => h.key))),
  secondaryClass: subClass,
  tertiaryClass: subClass,
  properShippingName: vOptional(vString()),
  technicalName: vOptional(vString()),
  reportableQuantity: vOptional(vBoolean()),
  residueLastContained: vOptional(vBoolean()),
  limitedQuantity: vOptional(vBoolean()),
  marinePollutant: vOptional(vBoolean()),
})

const vAtLeastOne = () => vPipe(vStringToNumber(), vMinValue(1))
const vAtLeastOneInt = () => vPipe(vStringToNumber(), vInt(), vMinValue(1))

const weight = vAtLeastOne()
const quantity = vAtLeastOneInt()
const weightUOM = vWeightUOM
const description = vString()

const baseLoadItemSchema = vObject({
  _id: vPipe(
    vOptional(vObjectId()),
    vTransform(id => ObjectId.createFromHexString(id)),
  ),
  customsValue: vOptional(vAtLeastOne()),
  customsValueCurrency: vOptional(vCurrency('usd')),
  declaredValue: vOptional(vAtLeastOne()),
  declaredValueCurrency: vOptional(vCurrency('usd')),
  dimensionsUOM: vOptional(vPicklist(dimensionsUOM)),
  freightClass: vOptional(vPicklist(freightClasses)),
  height: vOptional(vAtLeastOne()),
  length: vOptional(vAtLeastOne()),
  nmfcNumber: vOptional(vStringToNumber()),
  nmfcSubNumber: vOptional(vStringToNumber()),
  quantity: vOptional(quantity),
  refNums: vOptional(
    vArray(
      vObject({
        type: vPicklist(itemRefNumTypes),
        value: vString(),
        unitIndex: vOptional(vPipe(vStringToNumber(), vInt())),
      }),
    ),
  ),
  width: vOptional(vAtLeastOne()),
  weight: vOptional(weight),
  weightUOM: vOptional(weightUOM),
  description: vOptional(description),
  type: vOptional(vPicklist(packagingTypes.map(p => p.key))),
  hazardous: vOptional(vArray(baseLoadItemHazDetailSchema)),
})

export const loadHandlingUnitSchema = vObject({
  ...baseLoadItemSchema.entries,
  weight,
  weightUOM,
  description,
  quantity,
  handlingUnitId: vOptional(vObjectId()),
  type: vPicklist(packagingTypes.map(p => p.key)),
  pickupLocationSequence: vPipe(vStringToNumber(), vInt(), vMinValue(0)),
  dropLocationSequence: vAtLeastOneInt(),
  stackable: vOptional(vBoolean()),
  nonStandard: vOptional(vBoolean()),
  contains: vOptional(vArray(baseLoadItemSchema)),
})
export type LoadHandlingUnitDTO = vOutput<typeof loadHandlingUnitSchema>

export const MAX_ITEM_COUNT = 35
